import { cn } from "@veil/ui/lib/utils";
import X from "lucide-react/dist/esm/icons/x";
import { type KeyboardEvent, useState } from "react";

export function ChipInput({
	values,
	onChange,
	disabled,
	placeholder,
}: {
	values: string[];
	onChange: (v: string[]) => void;
	disabled?: boolean;
	placeholder?: string;
}) {
	const [draft, setDraft] = useState("");

	const commit = (raw: string) => {
		const v = raw.trim();
		if (!v) return;
		if (values.includes(v)) {
			setDraft("");
			return;
		}
		onChange([...values, v]);
		setDraft("");
	};

	const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();
			commit(draft);
		} else if (e.key === "Backspace" && !draft && values.length > 0) {
			onChange(values.slice(0, -1));
		}
	};

	return (
		<div
			className={cn(
				"flex min-h-10 flex-wrap items-center gap-2 rounded-lg border border-input bg-background px-3 py-2 text-sm transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50",
				disabled && "opacity-60",
			)}
		>
			{values.map((v) => (
				<span
					key={v}
					className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs"
				>
					{v}
					{!disabled && (
						<button
							type="button"
							onClick={() => onChange(values.filter((x) => x !== v))}
							className="hover:text-foreground"
							aria-label={`Remove ${v}`}
						>
							<X className="h-3 w-3" />
						</button>
					)}
				</span>
			))}
			{!disabled && (
				<input
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={onKey}
					onBlur={() => commit(draft)}
					placeholder={values.length === 0 ? placeholder : ""}
					className="min-h-7 min-w-[8ch] grow bg-transparent py-0.5 pl-0.5 pr-1 outline-none"
				/>
			)}
		</div>
	);
}
